import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon } from '@/core/constants/webComponents';
import { useMultipleEdition } from '../../contexts/MultipleEditionContext';
import { SENTIMENT_ICONS, GENDER_ICONS, SENTIMENT_OPTIONS, GENDER_OPTIONS, SENTIMENT_COLORS } from '../ClassificationCard/types';
import { ClassificationConfirmDialog } from '../ClassificationCard/components/ClassificationConfirmDialog';
import selectorService from '@/core/services/SelectorService';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import { ItemSelector } from '@/core/components/ItemSelector/ItemSelector';
import './MultipleEditionSelectors.scss';

export const MultipleEditionSelectors: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const {
    groupsSelection: groups, setGroupsSelection: setGroups,
    themesSelection: themes, setThemesSelection: setThemes,
    interestsSelection: interests, setInterestsSelection: setInterests,
    tagsSelection: tags, setTagsSelection: setTags,
    sentimentSelection, setSentimentSelection,
    genderSelection, setGenderSelection,
    checkedActive, setCheckedActive,
    removeActive, setRemoveActive,
    allVisiblePostsAreSpam,
    availableGroups,
    availableThemes,
    availableInterests,
    availableTags,
    loadingAvailableItems,
    addTagsLocally,
    refetchAvailableTags,
  } = useMultipleEdition();
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [pendingTagCreate, setPendingTagCreate] = useState<string[] | null>(null);

  const allTags = useMemo(
    () => [...availableTags, ...customTags.filter((c) => !availableTags.includes(c))],
    [availableTags, customTags],
  );

  const handleCreateTag = (name: string) => {
    setPendingTagCreate([name]);
  };

  const handleCreateTags = (items: string[]) => {
    setPendingTagCreate(items);
  };

  const handleConfirmTagCreate = useCallback(async () => {
    const names = pendingTagCreate;
    if (!names || names.length === 0) return;
    setPendingTagCreate(null);
    try {
      await selectorService.addTags([...allTags, ...names]);
      // Add tags to local state immediately for instant UI feedback
      addTagsLocally(names);
      refetchAvailableTags().catch(() => {});
      setCustomTags((prev) => {
        const toAdd = names.filter((n) => !prev.includes(n));
        return [...prev, ...toAdd];
      });
      setTags((prev) => {
        const updated = { ...prev };
        names.forEach((n) => { updated[n] = 'add'; });
        return updated;
      });
      showToast({
        variant: 'success',
        title: t('classifyNotifications.addTagSuccess'),
        text: '',
        icon: 'checkball',
      }).catch((err: unknown) => { console.error('[MultipleEditionSelectors] Failed to show toast:', err); });
    } catch (err: unknown) {
      console.error('[MultipleEditionSelectors] Failed to add tags:', err);
      showToast({
        variant: 'error',
        title: t('classifyNotifications.addTagError'),
        text: '',
        icon: 'error',
      }).catch((err: unknown) => { console.error('[MultipleEditionSelectors] Failed to show toast:', err); });
    }
  }, [pendingTagCreate, allTags, setTags, showToast, t, addTagsLocally, refetchAvailableTags]);

  const handleCancelTagCreate = useCallback(() => {
    setPendingTagCreate(null);
  }, []);

  return (
    <>
      {pendingTagCreate !== null && (
        <ClassificationConfirmDialog
          titleKey="classificationItemModal.confirmCreateTitle"
          messageKey={
            pendingTagCreate.length === 1
              ? 'classificationItemModal.confirmCreateMessage'
              : 'classificationItemModal.confirmCreateTagsMessage'
          }
          messageParams={
            pendingTagCreate.length === 1
              ? { item: pendingTagCreate[0] }
              : { items: pendingTagCreate.map((n) => `"${n}"`).join(', ') }
          }
          confirmKey="classificationItemModal.confirmCreateYes"
          cancelKey="classificationItemModal.confirmCreateNo"
          titleId="me-selectors-confirm-create-tag"
          onConfirm={handleConfirmTagCreate}
          onCancel={handleCancelTagCreate}
        />
      )}
      <div className="me-selectors">
      <div className="me-selectors__row">
        <ItemSelector
          label={t('classificationCard.groups')}
          placeholder={t('multipleEdition.notClassified')}
          selectAllLabel={t('multipleEdition.selectAll')}
          availableItems={availableGroups}
          availableLoading={loadingAvailableItems}
          selection={groups}
          onChange={setGroups}
        />
        <ItemSelector
          label={t('classificationCard.themes')}
          placeholder={t('multipleEdition.notClassified')}
          selectAllLabel={t('multipleEdition.selectAll')}
          availableItems={availableThemes}
          availableLoading={loadingAvailableItems}
          selection={themes}
          onChange={setThemes}
        />
      </div>
      <div className="me-selectors__row">
        <ItemSelector
          label={t('classificationCard.interests')}
          placeholder={t('multipleEdition.interestsPlaceholder')}
          selectAllLabel={t('multipleEdition.selectAll')}
          availableItems={availableInterests}
          availableLoading={loadingAvailableItems}
          selection={interests}
          onChange={setInterests}
        />
      </div>
      <div className="me-selectors__row">
        <ItemSelector
          label={t('classificationCard.tags')}
          placeholder={t('multipleEdition.tagsPlaceholder')}
          selectAllLabel={t('multipleEdition.selectAll')}
          availableItems={allTags}
          availableLoading={loadingAvailableItems}
          selection={tags}
          onChange={setTags}
          canCreate
          onCreateItem={handleCreateTag}
          onCreateItems={handleCreateTags}
        />
      </div>
      <div className="me-selectors__button-groups-row">
        <div className="me-selectors__button-group-section">
          <span className="me-selectors__button-group-label">{t('multipleEdition.sentiment')}</span>
          <div className="me-selectors__classification-group">
            {SENTIMENT_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`me-selectors__classification-btn me-selectors__classification-btn--${option}${
                  sentimentSelection === option ? ' me-selectors__classification-btn--active' : ''
                }`}
                onClick={() => setSentimentSelection((prev) => (prev === option ? null : option))}
              >
                <BdsIcon name={SENTIMENT_ICONS[option]} size="medium" color={SENTIMENT_COLORS[option]} />
              </button>
            ))}
          </div>
        </div>
        <div className="me-selectors__button-group-section">
          <span className="me-selectors__button-group-label">{t('multipleEdition.profileType')}</span>
          <div className="me-selectors__classification-group">
            {GENDER_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`me-selectors__classification-btn me-selectors__classification-btn--${option}${
                  genderSelection === option ? ' me-selectors__classification-btn--active' : ''
                }`}
                onClick={() => setGenderSelection((prev) => (prev === option ? null : option))}
              >
                <BdsIcon name={GENDER_ICONS[option]} size="medium" color="rgba(30, 107, 241, 1)" />
              </button>
            ))}
          </div>
        </div>
        <div className="me-selectors__action-buttons">
          <button
            className={`me-selectors__toggle-btn${checkedActive ? ' me-selectors__toggle-btn--active' : ''}`}
            onClick={() => setCheckedActive((v) => !v)}
          >
            <BdsIcon name="check" size="medium" color="rgba(30, 107, 241, 1)" />
            {t('multipleEdition.checkedButton')}
          </button>
          <button
            className={`me-selectors__toggle-btn ${removeActive ? ' me-selectors__toggle-btn--active' : '' }${allVisiblePostsAreSpam ? ' me-selectors__toggle-btn--restore' : ' me-selectors__toggle-btn--spam'}`}
            onClick={() => setRemoveActive((v) => !v)}
          >
            {allVisiblePostsAreSpam ? (
              <BdsIcon name="redo" size="medium" color="rgba(30, 107, 241, 1)" />
            ) : (
              <BdsIcon name="trash" size="small" color="var(--color-negative, rgba(168, 11, 11, 1))" />
            )}
            {allVisiblePostsAreSpam ? t('multipleEdition.restoreButton') : t('multipleEdition.removeButton')}
          </button>
        </div>
      </div>
    </div>
    </>
  );
};
